import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, MessageCircle, CheckCircle, Cpu, Shield, Wifi, Printer, Monitor,
  Wrench, ShieldCheck, Zap, HardDrive, MapPin, Clock, Star,
  ChevronDown, ChevronUp, ArrowRight, Home
} from 'lucide-react';
import { APP_CONFIG } from '../../config/app';
import { useSeoMeta } from '../../hooks/useSeoMeta';
import { SITE_URL } from '../../config/business';
import { buildFaqSchema, buildServiceSchema } from '../../utils/structuredData';
import type { FaqEntry } from '../../utils/structuredData';

import TrackedContactLink from '../../components/TrackedContactLink';
export interface NearbyTown {
  name: string;
  link?: string;
}

export interface InternalLink {
  label: string;
  href: string;
}

export interface LocalSeoPageProps {
  city: string;
  cityShort?: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  nearbyTowns: NearbyTown[];
  internalLinks: InternalLink[];
  postalCode?: string;
  /** Questions FAQ propres à la ville, affichées avant les questions génériques. */
  cityFaqs?: FaqEntry[];
  /** Ex. « à 10 min de Viry ». */
  travelTime?: string;
  /** Lien interne vers la page réparation téléphone correspondante. */
  phoneRepairLink?: InternalLink;
  /**
   * Zone plutôt qu'une commune (ex. { preposition: 'dans le', name: 'Genevois français' }) :
   * remplace « à {city} » dans le H1, les titres et les textes.
   */
  area?: { preposition: string; name: string };
}

const MAX_FAQS = 6;
const SEO_IMAGE = `${SITE_URL}/images/heroes/depan-hero.png`;

const PHONE = APP_CONFIG.contact.phoneTel;
const PHONE_DISPLAY = APP_CONFIG.contact.phoneDisplay;
const WHATSAPP = APP_CONFIG.contact.whatsappUrl;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-line rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left text-fg font-medium hover:bg-surface transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span>{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-accent-soft flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-accent-soft flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-fg-muted text-sm leading-relaxed bg-surface">
          {answer}
        </div>
      )}
    </div>
  );
};

const CtaBlock: React.FC<{ inPlace: string }> = ({ inPlace }) => (
  <div className="card p-6 md:p-8 my-8 text-center">
    <p className="text-fg font-semibold mb-1">Un problème informatique {inPlace} ?</p>
    <p className="text-fg-muted text-sm mb-4">Diagnostic gratuit — réponse en moins de 2h, intervention souvent le jour même.</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <TrackedContactLink channel="phone" service="depannage" ctaLocation="hero" href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 btn-primary !px-5 !py-2.5 !text-sm">
        <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
      </TrackedContactLink>
      <TrackedContactLink channel="whatsapp" service="depannage" ctaLocation="hero" href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 btn-secondary !px-5 !py-2.5 !text-sm">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </TrackedContactLink>
    </div>
  </div>
);

const LocalSeoPage: React.FC<LocalSeoPageProps> = ({
  city,
  cityShort,
  slug,
  metaTitle,
  metaDescription,
  intro,
  nearbyTowns,
  internalLinks,
  postalCode,
  cityFaqs = [],
  travelTime,
  phoneRepairLink,
  area,
}) => {
  const displayCity = cityShort || city;
  const preposition = area ? area.preposition : 'à';
  const place = area ? area.name : city;
  const inPlace = `${preposition} ${place}`;
  const inPlaceShort = area ? inPlace : `à ${displayCity}`;
  // Zone : la liste nearbyTowns contient ses propres communes, pas des villes voisines.
  const nearbyHeading = area
    ? `Les communes où j'interviens ${inPlace}`
    : `J'interviens également dans les villes proches de ${displayCity}`;
  const nearbyIntro = area
    ? `Voici les communes où j'interviens régulièrement ${inPlace} :`
    : `Vous habitez dans une commune voisine ? Pas de problème. Voici les villes où nous intervenons régulièrement autour de ${displayCity} :`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const genericFaqs: FaqEntry[] = [
    {
      question: `Combien coûte un dépannage informatique ${inPlaceShort} ?`,
      answer: `Le diagnostic est toujours gratuit. Le déplacement à domicile est inclus. Le tarif dépend ensuite du type de panne. Nous vous donnons un devis clair avant toute intervention, sans mauvaise surprise.`,
    },
    {
      question: `Intervenez-vous à domicile ${inPlaceShort} ?`,
      answer: `Oui. Virus, PC lent, problème Windows, imprimante, panne matérielle... Nous venons chez vous (particuliers) ou sur site (entreprises) pour un diagnostic gratuit, puis un devis avant toute intervention. Le déplacement est inclus, garantie 90 jours, et problème non résolu = remboursé.`,
    },
    {
      question: `Intervenez-vous dans les communes ${area ? `du ${area.name}` : `autour de ${displayCity}`} ?`,
      answer: `Absolument. Nous intervenons ${inPlace} et dans toutes les communes voisines : ${nearbyTowns.map(t => t.name).join(', ')}.`,
    },
    {
      question: 'Que faire si mon ordinateur est très lent ?',
      answer: `Ne paniquez pas, c'est l'un des problèmes les plus courants et les plus faciles à résoudre. Les causes habituelles sont un disque dur presque plein, des logiciels qui démarrent automatiquement, ou un virus. Contactez-nous pour un diagnostic gratuit et on vous explique la marche à suivre.`,
    },
    {
      question: 'Proposez-vous un diagnostic gratuit ?',
      answer: `Oui, le diagnostic est 100 % gratuit et sans engagement. Décrivez-nous votre problème par WhatsApp ou par téléphone, et nous vous donnons un premier avis immédiatement. Si une intervention est nécessaire, vous recevez un devis clair avant qu'on commence quoi que ce soit.`,
    },
  ];

  const faqs: FaqEntry[] = [
    ...cityFaqs,
    ...genericFaqs.slice(0, Math.max(0, MAX_FAQS - cityFaqs.length)),
  ];

  useSeoMeta({
    title: metaTitle,
    description: metaDescription,
    path: `/${slug}`,
    image: SEO_IMAGE,
    jsonLd: [
      buildServiceSchema({
        name: `Dépannage informatique ${inPlace}`,
        serviceType: 'Dépannage informatique',
        path: `/${slug}`,
        description: metaDescription,
        cities: area ? nearbyTowns.map((t) => t.name) : [city, ...nearbyTowns.map((t) => t.name)],
      }),
      buildFaqSchema(faqs),
    ],
  });

  return (
    <div className="min-h-screen bg-canvas text-fg">

      {/* ── 1. Hero ── */}
      <section className="relative pt-28 md:pt-36 pb-12 px-4 bg-canvas hero-halo">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <span className="inline-flex items-center gap-2 section-subtitle-badge">
              <MapPin className="w-4 h-4" /> Service local — {place}{postalCode ? ` (${postalCode})` : ''}
            </span>
          </motion.div>

          <h1
            className="heading-display mb-6"
          >
            Dépannage informatique {preposition} <span className="text-accent-soft">{place}</span>
          </h1>

          <motion.div initial="hidden" animate="visible" custom={2} variants={fadeUp}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <TrackedContactLink channel="phone" service="depannage" ctaLocation="hero" href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 btn-primary">
                <Phone className="w-5 h-5" /> Appeler maintenant
              </TrackedContactLink>
              <TrackedContactLink channel="whatsapp" service="depannage" ctaLocation="hero" href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 btn-secondary">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </TrackedContactLink>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-fg-muted">
              {['Diagnostic gratuit', 'Déplacement inclus', 'Intervention à domicile ou sur site'].map(badge => (
                <span key={badge} className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-accent-soft" /> {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Contenu principal ── */}
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-16">

        {/* 1. Présentation du service */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-5">
            Technicien informatique {preposition} <span className="text-accent-soft">{place}</span>
          </h2>
          <div className="text-fg-muted leading-relaxed space-y-4">
            <p>{intro}</p>
            <p>
              Chez SOS IT Services, nous intervenons <strong className="text-fg">à domicile</strong> pour les particuliers
              et <strong className="text-fg">sur site</strong> pour les entreprises, {inPlace} et dans les communes voisines.
              Réparation d'ordinateur, remplacement de composant, suppression de virus, configuration réseau :
              le technicien se déplace chez vous, le déplacement est inclus.
            </p>
            <p>
              Nous intervenons pour les particuliers comme pour les professionnels.
              Que vous ayez besoin d'un dépannage urgent ou d'un suivi régulier,
              notre équipe s'adapte à votre emploi du temps.
              Un devis clair vous est toujours communiqué avant toute intervention.
            </p>
          </div>
          <CtaBlock inPlace={inPlaceShort} />
        </motion.section>

        {/* 2. Problèmes informatiques courants */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-5">
            Problèmes informatiques courants {inPlaceShort}
          </h2>
          <div className="text-fg-muted leading-relaxed space-y-4">
            <p>
              Voici les pannes que nos clients {inPlace} nous signalent le plus fréquemment.
              Dans la grande majorité des cas, elles se résolvent rapidement lors d'une intervention à domicile.
            </p>
          </div>
          <div className="mt-6 space-y-4">
            {[
              {
                icon: Cpu,
                title: 'Ordinateur lent',
                text: `Votre PC met plusieurs minutes à démarrer ou rame dès que vous ouvrez un programme ? C'est l'une des pannes les plus fréquentes. Les causes sont souvent un disque dur saturé, trop de logiciels au démarrage, ou un virus discret qui consomme vos ressources en arrière-plan.`,
              },
              {
                icon: Shield,
                title: 'Virus ou malware',
                text: `Des publicités qui s'ouvrent seules, un navigateur qui redirige vers des sites inconnus, des fichiers qui disparaissent — ce sont les signes d'une infection. Ne touchez à rien et appelez-nous. Nous faisons une analyse complète et nettoyons votre système en profondeur.`,
              },
              {
                icon: Wifi,
                title: 'Problème WiFi',
                text: `Internet qui coupe toutes les 5 minutes, un réseau qui n'apparaît plus, ou une connexion lente malgré un bon débit ? Le problème vient souvent du pilote réseau, de la configuration de votre box ou d'un conflit d'adresse IP. On diagnostique et on règle sur place.`,
              },
              {
                icon: Printer,
                title: 'Imprimante non reconnue',
                text: `Votre imprimante n'est plus détectée après une mise à jour Windows ? C'est un classique. Pilote corrompu, port USB mal configuré, service d'impression bloqué — on connaît ces problèmes par cœur et on les résout rapidement.`,
              },
              {
                icon: Monitor,
                title: 'Windows bloqué ou écran bleu',
                text: `Votre PC affiche un écran bleu au démarrage, tourne en boucle sur les mises à jour, ou reste bloqué sur le logo Windows ? Ces situations font peur mais sont souvent réparables sans perte de données. Contactez-nous avant de tenter une réinstallation.`,
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-surface rounded-xl p-5 border border-line">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-6 h-6 text-accent-soft flex-shrink-0" />
                  <h3 className="font-semibold text-fg">{title}</h3>
                </div>
                <p className="text-fg-muted text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* 3. Comment ça marche */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-5">
            Comment se déroule une intervention {inPlaceShort} ?
          </h2>
          <div className="text-fg-muted leading-relaxed space-y-4">
            <p>
              Du premier contact à la réparation, le parcours est simple : vous nous contactez,
              je vous réponds en moins de 2h, puis je me déplace chez vous, souvent le jour même.
              Le diagnostic est gratuit et un devis vous est remis avant toute intervention.
            </p>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Phone, label: '1. Prise de contact', desc: 'Par téléphone ou WhatsApp' },
              { icon: Clock, label: '2. Réponse en moins de 2h', desc: 'Nous convenons ensemble d’un créneau' },
              { icon: Home, label: '3. Intervention à domicile', desc: 'Déplacement inclus' },
              { icon: CheckCircle, label: '4. Diagnostic gratuit et devis', desc: 'Devis clair avant toute intervention' },
              { icon: Wrench, label: '5. Réparation', desc: 'Garantie 90 jours, problème non résolu = remboursé' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-surface rounded-xl p-4 border border-line text-center">
                <Icon className="w-7 h-7 text-accent-soft mx-auto mb-2" />
                <p className="text-fg font-medium text-sm">{label}</p>
                <p className="text-fg-muted text-xs mt-1">{desc}</p>
              </div>
            ))}
          </div>
          <CtaBlock inPlace={inPlaceShort} />
        </motion.section>

        {/* 4. Intervention à domicile */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-5">
            Intervention à domicile {inPlace}
          </h2>
          <div className="text-fg-muted leading-relaxed space-y-4">
            <p>
              Certaines pannes nécessitent une présence physique : remplacement de composant,
              installation d'imprimante, configuration réseau, ou simplement si vous préférez
              qu'un technicien soit à vos côtés pour vous expliquer ce qui est fait.
            </p>
            <p>
              Nous intervenons à domicile {inPlace}{travelTime ? ` (${travelTime})` : ''} et plus largement {area ? 'dans ' : 'dans le Genevois français, '}
              le Pays de Gex et la région de Genève.
              Le déplacement est inclus dans le tarif, sans frais cachés.
              Nous nous adaptons à vos disponibilités, y compris le soir ou le week-end selon notre planning.
            </p>
            <p>
              Nos services à domicile couvrent notamment :
            </p>
            <ul className="space-y-2 mt-2">
              {[
                { icon: Wrench, text: 'Dépannage et réparation ordinateur (PC, Mac, portable)' },
                { icon: ShieldCheck, text: 'Suppression de virus et sécurisation de votre système' },
                { icon: Zap, text: 'Optimisation et accélération de votre PC' },
                { icon: HardDrive, text: 'Récupération de données sur disque dur en panne' },
                { icon: Home, text: 'Configuration réseau, box internet et WiFi' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2 text-fg-muted text-sm">
                  <Icon className="w-4 h-4 text-accent-soft mt-0.5 flex-shrink-0" />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* 5. Villes proches */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-4">
            {nearbyHeading}
          </h2>
          <p className="text-fg-muted mb-6">{nearbyIntro}</p>
          <div className="flex flex-wrap gap-3 mb-8">
            {nearbyTowns.map(({ name, link }) => (
              link ? (
                <Link
                  key={name}
                  to={link}
                  className="flex min-h-11 items-center gap-1.5 bg-surface border border-line hover:border-line-strong text-fg-muted hover:text-fg px-4 py-2 rounded-full text-sm transition-colors md:min-h-0"
                >
                  <MapPin className="w-3.5 h-3.5 text-accent-soft" /> {name}
                </Link>
              ) : (
                <span key={name} className="flex items-center gap-1.5 bg-surface border border-line text-fg-muted px-4 py-2 rounded-full text-sm">
                  <MapPin className="w-3.5 h-3.5 text-accent-soft" /> {name}
                </span>
              )
            ))}
          </div>
          {phoneRepairLink && (
            <Link
              to={phoneRepairLink.href}
              className="flex min-h-11 items-center gap-2 text-accent-soft hover:text-accent-soft text-sm transition-colors md:min-h-0 mb-2"
            >
              <ArrowRight className="w-4 h-4 flex-shrink-0" /> Besoin de réparer votre téléphone {inPlaceShort} ? {phoneRepairLink.label}
            </Link>
          )}
          {internalLinks.length > 0 && (
            <div className="space-y-2">
              {internalLinks.map(({ label, href }) => (
                <Link
                  key={href}
                  to={href}
                  className="flex min-h-11 items-center gap-2 text-accent-soft hover:text-accent-soft text-sm transition-colors md:min-h-0"
                >
                  <ArrowRight className="w-4 h-4 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          )}
        </motion.section>

        {/* FAQ */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="heading-section mb-6">
            Questions fréquentes — dépannage informatique {inPlaceShort}
          </h2>
          <div className="space-y-3">
            {faqs.map(faq => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </motion.section>

      </div>

      {/* CTA final */}
      <section className="section-spacing px-4 bg-canvas border-t border-line">
        <div className="max-w-2xl mx-auto text-center">
          <Star className="w-8 h-8 text-accent-soft mx-auto mb-4" />
          <h2 className="heading-section mb-3">
            Votre ordinateur pose problème {inPlace} ?
          </h2>
          <p className="text-fg-muted mb-6">
            Contactez-nous maintenant. Diagnostic gratuit, réponse en moins de 2h,
            intervention souvent le jour même, à domicile ou sur site selon votre besoin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <TrackedContactLink channel="phone" service="depannage" ctaLocation="cta" href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 btn-primary !px-8">
              <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
            </TrackedContactLink>
            <TrackedContactLink channel="whatsapp" service="depannage" ctaLocation="cta" href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 btn-secondary !px-8">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </TrackedContactLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocalSeoPage;
